"use client";

import { useState, useCallback, useMemo, type ReactNode, type RefObject } from "react";
import { useTranslations } from "use-intl";

import { CloseIcon } from "@/components/appIcons";
import { SearchableSelectSurface } from "@/components/ui/searchable-select-surface";
import { useFormatNumber } from "@/hooks/use-format-number";

interface FacetItem {
  value: string;
  count: number;
}

interface FacetSelectProps {
  items: FacetItem[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  className?: string;
  /** Custom label renderer. Receives the raw value; defaults to displaying value as-is. */
  renderLabel?: (value: string) => ReactNode;
  /** Custom search matcher. Return true if the item matches the query. Defaults to case-insensitive includes on value. */
  matchesSearch?: (item: FacetItem, query: string) => boolean;
  filterSearchItems?: boolean;
  onSearchChange?: (value: string) => void;
  disabled?: boolean;
}

export function FacetSelect({
  items,
  value,
  onChange,
  placeholder,
  className,
  renderLabel,
  matchesSearch,
  filterSearchItems = true,
  onSearchChange,
  disabled,
}: FacetSelectProps) {
  const formatNumber = useFormatNumber();
  const ta = useTranslations("globals.accessibility");
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const label = (v: string) => renderLabel?.(v) ?? v;

  const filtered = useMemo(() => {
    if (!inputValue || !filterSearchItems) return items;
    const q = inputValue.toLowerCase();
    return items.filter((item) =>
      matchesSearch ? matchesSearch(item, q) : item.value.toLowerCase().includes(q),
    );
  }, [filterSearchItems, items, inputValue, matchesSearch]);

  const close = useCallback(() => {
    setIsOpen(false);
    setInputValue("");
    onSearchChange?.("");
    setSelectedIndex(-1);
  }, [onSearchChange]);

  const setOpenState = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setIsOpen(true);
      } else {
        close();
      }
    },
    [close],
  );

  const select = useCallback(
    (v: string) => {
      onChange(v);
      close();
    },
    [close, onChange],
  );

  const clear = useCallback(() => {
    onChange(undefined);
    close();
  }, [close, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === "Enter" || e.key === "ArrowDown") {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filtered.length) {
            select(filtered[selectedIndex].value);
          }
          break;
        case "Escape":
          close();
          break;
      }
    },
    [close, filtered, isOpen, selectedIndex, select],
  );

  const renderInput = (ref: RefObject<HTMLInputElement | null>, mode: "desktop" | "mobile") => (
    <div className={mode === "mobile" ? "p-2 border-b border-border" : undefined}>
      <input
        ref={ref}
        type="text"
        value={inputValue}
        placeholder={placeholder}
        autoFocus
        onChange={(e) => {
          setInputValue(e.target.value);
          onSearchChange?.(e.target.value);
          setSelectedIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        className={
          mode === "mobile"
            ? "w-full max-sm:min-h-11 px-2.5 py-2 bg-secondary border border-border rounded-md text-sm focus:outline-none"
            : "w-full bg-transparent focus:outline-none text-sm"
        }
      />
    </div>
  );

  const renderOptions = (ref: RefObject<HTMLUListElement | null>, className: string) => (
    <ul ref={ref} className={className}>
      {filtered.length === 0 ? (
        <li className="px-3 py-2 text-sm text-muted-foreground">--</li>
      ) : (
        filtered.map((item, i) => (
          <li key={item.value}>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                select(item.value);
              }}
              className={`w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center justify-between gap-2 ${
                i === selectedIndex
                  ? "bg-primary text-primary-foreground"
                  : item.value === value
                    ? "bg-primary/10"
                    : "hover:bg-secondary"
              }`}
            >
              <span className="truncate">{label(item.value)}</span>
              <span
                className={`text-[10px] flex-shrink-0 ${
                  i === selectedIndex ? "text-primary-foreground/70" : "text-muted-foreground"
                }`}
              >
                {formatNumber(item.count)}
              </span>
            </button>
          </li>
        ))
      )}
    </ul>
  );

  return (
    <SearchableSelectSurface
      open={isOpen}
      onOpenChange={setOpenState}
      title={placeholder}
      shouldFocus
      selectedIndex={selectedIndex}
      renderDesktopInputInPanel={false}
      desktopPanelClassName="w-[min(22rem,calc(100vw-1rem))] bg-card rounded-md"
      desktopOptionsClassName="max-h-48 overflow-y-auto"
      mobilePanelClassName="bg-card"
      renderTrigger={({ open, setOpen, focusInput, desktopInputRef }) =>
        value ? (
          <div className={className}>
            <div className="flex items-center gap-2 text-sm">
              <span className="flex-1 truncate">{label(value)}</span>
              <button
                type="button"
                aria-label={ta("clear")}
                onClick={clear}
                className="text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <CloseIcon className="text-[14px]" />
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => {
              if (disabled) return;
              setOpen(true);
              setTimeout(focusInput, 0);
            }}
            className={className}
          >
            {open ? (
              <>
                <div className="hidden md:block">{renderInput(desktopInputRef, "desktop")}</div>
                <span className="text-sm text-muted-foreground md:hidden">{placeholder}</span>
              </>
            ) : (
              <span
                className={`text-sm ${disabled ? "text-muted-foreground/50" : "text-muted-foreground"}`}
              >
                {placeholder}
              </span>
            )}
          </div>
        )
      }
      renderInput={renderInput}
      renderOptions={renderOptions}
    />
  );
}
